/**
 * Walk it through, step 4: this room's follow-ups, the tag automations its
 * people get. A calm list; one opens in the flow, edited with its own controls
 * (never by chat). "Add a follow-up" writes a ready-made one from the talk.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { LibraryKind } from "../../../shared/library-kind";
import type { BoardCards, BoardState } from "../../../shared/types";
import { offerFrom } from "../../../shared/offer";
import { friendlyTokens } from "../../../shared/tokens";
import { FlowEditor, TagChip, type FlowOutline, type MailContext } from "../../components/FlowEditor";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { useSetup } from "../../setup/SetupContext";
import { FOLLOW_UP_LIBRARY, libraryTaken } from "../followups";
import type { BoardHost } from "../host";
import { findCard } from "../state";
import { flipFollowUp, type Mail } from "./followRows";


/** The tag a ready-made follow-up starts on, said under its name in the menu. */
const STARTS_ON: Record<LibraryKind, string> = {
  reminder: "registered",
  unwatched: "registered",
  no_click: "reached-offer",
  thanks: "completed",
};

/**
 * While the business's email is not set up, its follow-up emails wait for it (specs/formal/email-wait): they go
 * out once the sending address verifies. Who can finish setup gets the way there.
 */
export function EmailWait() {
  const { state, canEdit } = useSetup();
  if (!state || state.email) return null;
  return (
    <div className="bw-wait sm" role="status">
      <Icon name="clock" />
      <span className="sp">Waiting for your email setup</span>
      {canEdit ? (
        <Link className="link" to="/setup/email">
          Finish email setup <Icon name="right" />
        </Link>
      ) : null}
    </div>
  );
}

/**
 * `phone`: the follow-up is seen as on a phone, an outline whose rows open each step full width, drawn in a phone as
 * the room and the page are.
 */
export function WalkFollow({ state, host, phone }: { state: BoardState; host: BoardHost; phone: boolean }) {
  const [menu, setMenu] = useState(false);
  // The follow-up just started from a tag: it opens on its When card.
  const [fromTag, setFromTag] = useState<string | null>(null);
  // A room with a session (a live, weekly or one-time) has a time a wait before it means something.
  const sessions = host.server.scheduleMode !== undefined && host.server.scheduleMode !== "on_demand";
  const [step, setStep] = useState<string | null>(null);
  // On a phone the flow is the outline: a step opens full width, as on the Automations page.
  const outline: FlowOutline | undefined = phone ? { open: step, onOpen: setStep, onClose: () => setStep(null) } : undefined;
  const mails = state.mail.filter((c): c is Mail => c.kind === "mail");
  const selected = host.selected ? findCard(state, host.selected) : undefined;
  const open = selected && selected.kind === "mail" ? selected : undefined;
  const offerCard = state.room.find((c) => c.kind === "offer") as BoardCards["offer"] | undefined;
  const mail: MailContext = {
    webinarTitle: host.server.title,
    hostName: host.server.hostName,
    workspaceName: host.server.workspaceName ?? "",
    offer: offerCard?.on ? offerFrom(offerCard) : null,
    brand: host.server.brand,
  };
  /** What a follow-up starts on and what it does, as the prototype's list says it. */
  const rowLine = (m: Mail) => {
    const row = m.automationId ? host.followUpRows.get(m.automationId) : undefined;
    if (!row) return m.subject ? friendlyTokens(m.subject) : "No subject yet";
    const emails = row.graph.nodes.filter((n) => n.type === "email").length;
    const has = (type: string) => row.graph.nodes.some((n) => n.type === type);
    const did = emails
      ? `${emails} ${emails === 1 ? "email" : "emails"}`
      : has("notify")
        ? "notifies a person"
        : has("room")
          ? "adds to their page"
          : has("webhook")
            ? "sends to a webhook"
            : has("fact")
              ? "changes a tag"
              : "no steps yet";
    return (
      <>
        {row.trigger.type === "tag_removed" ? "Loses" : "Gets"} <TagChip name={row.trigger.value} />
        {row.trigger.how_often === "each" && sessions ? " · each session" : ""}
        {" · "}
        {did}
      </>
    );
  };
  /** The other rooms it runs on, as the prototype says it; empty when this room is its only one. */
  const alsoOn = (m: Mail): string => {
    const row = m.automationId ? host.followUpRows.get(m.automationId) : undefined;
    const names = (row?.rooms ?? []).filter((r) => r.enabled && r.webinar_id !== host.webinarId).map((r) => r.title);
    return names.length ? `Also runs on ${names.join(" and ")}` : "";
  };
  const also = (m: Mail) => {
    const words = alsoOn(m);
    return words ? (
      <em className="bw-also">
        <Icon name="link" />
        {words}
      </em>
    ) : null;
  };
  const startFromTag = () => {
    setMenu(false);
    void host.startFollowUp().then((card) => {
      if (!card) return;
      setFromTag(card.id);
      if (phone) setStep("trigger");
    });
  };
  const flip = (m: Mail) => flipFollowUp(host, m);
  const ready = FOLLOW_UP_LIBRARY.filter(([, , , when]) => sessions || when === "any");

  if (open) {
    const binding = host.followUp?.automationId === open.automationId ? host.followUp : null;
    return (
      <div className="bw-followstep">
        <EmailWait />
        <div className="bw-ahead">
          <button
            type="button"
            className="link"
            onClick={() => {
              setFromTag(null);
              host.select(null);
            }}
          >
            <Icon name="left" /> All follow-ups
          </button>
          <h3>{open.label}</h3>
          <span className="sp" />
          {also(open)}
          <span className="bw-aon">
            <button type="button" className={cx("toggle", open.on && "on")} role="switch" aria-checked={open.on} aria-label="On for this room" onClick={() => flip(open)} />
            <em>On for this room</em>
          </span>
        </div>
        {binding?.editor ? (
          <div className={cx(phone ? "bw-phwrap" : "bw-flow canvas fla-edit")}>
            <PhoneFrame on={phone}>
              <FlowEditor
                editor={binding.editor}
                setEditor={(next) => binding.setEditor(next)}
                onToast={(m) => host.toast(m)}
                undo={(m, back) => host.toast(m, back ? { label: "Undo", run: back } : undefined)}
                mail={mail}
                dirty={binding.dirty}
                onDone={() => binding.save()}
                onDiscard={() => binding.discard()}
                automationId={open.automationId ?? null}
                sessions={sessions}
                paused={!open.on}
                initialOpen={fromTag === open.id ? "trigger" : null}
                {...(outline ? { outline } : {})}
              />
            </PhoneFrame>
            {binding.error ? (
              <p className="note warn mt16" role="alert">
                <Icon name="info" />
                <span>{binding.error}</span>
              </p>
            ) : null}
          </div>
        ) : (
          <div className="card nbw nbload" aria-busy="true" aria-label="Opening the follow-up" />
        )}
      </div>
    );
  }

  return (
    <div className="bw-followstep">
      <EmailWait />
      <div className="bw-alist">
        {mails.map((m) => (
          <div
            key={m.id}
            className={cx("bw-arow", !m.on && "off", host.fresh.has(m.id) && "bw-isnew")}
            role="button"
            tabIndex={0}
            onClick={() => host.select(m.id)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && host.select(m.id)}
          >
            <span className="bw-aic">
              <Icon name="zap" />
            </span>
            <div className="sp">
              <b>{m.label}</b>
              <span>{rowLine(m)}</span>
              {also(m)}
            </div>
            <span className="bw-aon" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <button type="button" className={cx("toggle", m.on && "on")} role="switch" aria-checked={m.on} aria-label={`${m.label}, on for this room`} onClick={() => flip(m)} />
              <em>On for this room</em>
            </span>
            <Icon name="chev" />
          </div>
        ))}
        <div className="bw-aaddw">
          <button type="button" className={cx("bw-aadd", menu && "on")} aria-expanded={menu} disabled={!!host.busy} onClick={() => setMenu((x) => !x)}>
            <Icon name="plus" />
            <span>{host.busy?.op === "followup" ? "Writing it…" : "Add a follow-up"}</span>
          </button>
          {menu ? (
            <div className="fe-menu bw-amenu" role="menu">
              {ready.map(([name, request, kind]) => {
                const taken = libraryTaken(state, host.followUpTriggers, name, kind);
                return (
                  <button
                    type="button"
                    role="menuitem"
                    key={name}
                    disabled={taken}
                    onClick={() => {
                      setMenu(false);
                      host.addFollowUp(request, undefined, name);
                    }}
                  >
                    <Icon name="zap" />
                    <span>
                      {name}
                      <em>{taken ? "Already on this room" : `Gets ${STARTS_ON[kind]}`}</em>
                    </span>
                  </button>
                );
              })}
              <hr />
              <button type="button" role="menuitem" onClick={startFromTag}>
                <Icon name="tag" />
                <span>Start from a tag</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The phone drawn around what a guest or a host sees on one, when it is seen from a laptop. */
function PhoneFrame({ on, children }: { on: boolean; children: ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <div className="bw-phone bw-auto">
      <div className="bw-phone-in">{children}</div>
    </div>
  );
}
